import type { MetaFunction } from "@remix-run/cloudflare";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { DownloadButton } from "~/components/ui/download-button";
import { ProtectedDownloadLink } from "~/components/ui/protected-download-link";
import { 
  Download, 
  Eye, 
  Heart, 
  Calendar,
  User,
  Shield,
  Star,
  FileText,
  BarChart3,
  Github,
  Coffee,
  MessageSquare
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Mod Hub - Stellar Blade Mod Manager | stellarblademod.com" },
    { 
      name: "description", 
      content: "Download and manage Stellar Blade mods easily with Mod Hub - the ultimate mod manager application. Features automatic downloads, mod library management, and modern UI." 
    },
    { name: "keywords", content: "stellar blade mod manager, mod hub, stellar blade mods, mod management, UE4SS, nexus mods, mod installer" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/mods/mod-hub" },
    
    // Open Graph
    { property: "og:title", content: "Mod Hub - Stellar Blade Mod Manager" },
    { property: "og:description", content: "Download and manage Stellar Blade mods easily with Mod Hub - the ultimate mod manager application." },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://stellarblademod.com/mods/mod-hub" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Mod Hub - Stellar Blade Mod Manager" },
    { name: "twitter:description", content: "Download and manage Stellar Blade mods easily with Mod Hub - the ultimate mod manager application." },
  ];
};

export default function ModHub() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div 
        className="relative text-white py-12 border-b border-slate-800"
        style={{
          backgroundImage: "url('/mods/modhub2.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mod Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="bg-cyan-500/20 text-cyan-300 border-cyan-400/30">
                  User Interface
                </Badge>
                <Badge variant="secondary" className="bg-purple-500/20 text-purple-300 border-purple-400/30">
                  Mod Manager
                </Badge>
              </div>
              
              <h1 className="text-4xl font-bold mb-4">
                Mod Hub - Stellar Blade Mod Manager
              </h1>
              
              <p className="text-xl opacity-90 mb-6">
                The ultimate Nexus Mods manager for Stellar Blade. Seamlessly download, install, and manage your mod collection directly from the Nexus Mods website. Built with modern technologies and designed for effortless mod management.
              </p>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Created by <a href="https://next.nexusmods.com/profile/Dekita?gameId=7804" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">DekitaRPG</a></span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Updated: June 14, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>Version 0.10.10</span>
                </div>
              </div>
            </div>
            
            {/* Stats Card */}
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 text-white backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-cyan-300">
                    <BarChart3 className="h-5 w-5" />
                    Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Endorsements
                    </span>
                    <span className="font-bold">156</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Downloads
                    </span>
                    <span className="font-bold">2,347</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Views
                    </span>
                    <span className="font-bold">8,921</span>
                  </div>
                  <Separator className="bg-slate-600" />
                  <DownloadButton className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg" scrollOnly>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </DownloadButton>
                  <p className="text-xs opacity-80 text-center">
                    Manual download available
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-cyan-300">About this mod</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none text-slate-300">
                <div className="space-y-6">
                  {/* Features */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Features:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <Download className="h-4 w-4 mt-1 text-green-600" />
                        <span><strong>Direct Nexus Mods integration</strong> - Browse, download, and install mods seamlessly</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="h-4 w-4 mt-1 text-purple-600" />
                        <span><strong>One-click UE4SS installation</strong> - Essential framework for Stellar Blade mods</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-4 w-4 mt-1 text-orange-600" />
                        <span><strong>Complete mod library management</strong> - Enable, disable, and organize with ease</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Star className="h-4 w-4 mt-1 text-yellow-600" />
                        <span><strong>Stellar Blade themed interface</strong> - Multiple themes available for customization</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="h-4 w-4 mt-1 text-blue-600" />
                        <span><strong>Automatic updates</strong> - Always stay up-to-date with the latest features</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Heart className="h-4 w-4 mt-1 text-pink-600" />
                        <span><strong>User-friendly design</strong> - Beautiful and intuitive interface</span>
                      </li>
                    </ul>
                  </div>

                  <Separator />

                  {/* Installation */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Installation / How To Setup:</h3>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li><strong>Download & Install</strong> the latest app version</li>
                      <li><strong>Once downloaded</strong>, extract and run the installer</li>
                      <li><strong>UE Mod Hub is now installed</strong> on your pc, enjoy &lt;3</li>
                    </ol>
                  </div>

                  <Separator />

                  {/* Known Issues */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Known Issues:</h3>
                    <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded backdrop-blur-sm">
                      <p className="text-sm">
                        <strong>Usage Tips:</strong> Some mods may require manual download and installation. It's recommended to carefully read each mod's installation instructions before installing to ensure compatibility and correct installation order.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Player Review */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Player Experience:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        As a dedicated Stellar Blade player, this Mod Hub tool completely transforms the mod installation and management experience. Compared to the tedious process of manually downloading and installing mods, this application makes the entire workflow incredibly simple and intuitive.
                      </p>
                      <p className="text-sm mb-3">
                        The application features a modern interface design that perfectly matches the game's sci-fi aesthetic. The Stellar theme's color scheme particularly makes users feel immersed in the game world while using the tool. Automatic update functionality ensures users always have the latest version, while multiple theme options cater to different user preferences.
                      </p>
                      <p className="text-sm">
                        For newcomers, the tool's greatest advantage is simplifying the UE4SS installation and configuration process. Traditional manual installation methods are error-prone, while Mod Hub completes the entire setup with just a few clicks.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Usage Guide */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Usage Guide:</h3>
                    <div className="space-y-4">
                      <div className="bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-blue-300">1. Initial Setup</h4>
                        <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                          <li>After downloading the installer, users may see a Windows security warning</li>
                          <li>Click "More Info" then select "Run Anyway" to continue installation</li>
                          <li>Recommended to scan the file with <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Virus Total</a> before installation for security</li>
                        </ul>
                      </div>
                      
                      <div className="bg-green-900/20 border-l-4 border-green-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-green-300">2. Application Configuration</h4>
                        <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                          <li>Configure application settings to enable full functionality</li>
                          <li>Set mod download paths and game directory</li>
                          <li>Choose preferred themes and interface styles</li>
                        </ul>
                      </div>
                      
                      <div className="bg-purple-900/20 border-l-4 border-purple-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-purple-300">3. Nexus Mods Integration</h4>
                        <ul className="text-sm space-y-1 list-disc list-inside ml-4">
                          <li>Connect to Nexus Mods and browse the entire Stellar Blade mod library</li>
                          <li>Download mods directly from Nexus Mods with one-click installation</li>
                          <li>Manage mod load order and resolve conflicts automatically</li>
                          <li>Track mod updates and receive notifications for new versions</li>
                          <li>Sync your mod collection across different devices</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Additional Installation Help */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Technical Notes:</h3>
                    <div className="bg-slate-800/20 border border-slate-600 p-4 rounded backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        Since this application is independently developed software, the author has not purchased an expensive Windows code signing certificate (which costs $250 per year), so Windows may display security warnings. This is a normal occurrence and does not affect the software's security.
                      </p>
                      <p className="text-sm">
                        If you have concerns about file security, it's recommended to upload the setup.exe file to the <a href="https://www.virustotal.com/gui/home/upload" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Virus Total</a> website for multi-scan verification before installation. This website uses 50+ antivirus software to scan files and provides detailed security reports.
                      </p>
                    </div>
                  </div>

                  {/* Support Links */}
                  <div className="flex flex-wrap gap-4 pt-6">
                    <DownloadButton 
                      variant="outline" 
                      className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                      scrollOnly
                    >
                      <Download className="h-4 w-4" />
                      Download Latest Version
                    </DownloadButton>
                    <Button variant="outline" asChild>
                      <a href="/contact" className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800">
                        <MessageSquare className="h-4 w-4" />
                        Community Support
                      </a>
                    </Button>
                  </div>

                  <Separator />

                  {/* Original Page Link */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-slate-600 rounded-lg backdrop-blur-sm">
                    <h4 className="text-lg font-semibold mb-3 text-cyan-300">Visit Original Page</h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Check the official Nexus Mods page for latest updates, community discussions, and technical support.
                    </p>
                    <Button asChild className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0 shadow-lg">
                      <a 
                        href="https://www.nexusmods.com/stellarblade/mods/206?tab=description" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <Star className="h-4 w-4" />
                        View Nexus Mods Page
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* File Information */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">File Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-slate-300">
                  <div className="flex justify-between text-sm">
                    <span>Last updated</span>
                    <span>June 14, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Original upload</span>
                    <span>June 12, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Created by</span>
                    <a href="https://next.nexusmods.com/profile/Dekita?gameId=7804" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">DekitaRPG</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Uploaded by</span>
                    <a href="https://next.nexusmods.com/profile/Dekita?gameId=7804" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Dekita</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Virus scan</span>
                    <Badge variant="secondary" className="bg-green-900/50 text-green-300 border-green-600">
                      Safe to use
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Download Section */}
              <Card id="download-section" className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Download Files</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* File Information */}
                    <div className="p-4 border border-slate-600 rounded-lg">
                      <h4 className="font-semibold text-slate-200">Mod Hub Setup</h4>
                      <p className="text-sm text-slate-400">Main installer file</p>
                      <p className="text-xs text-slate-500">Version 0.10.10 • 111.4 MB</p>
                    </div>
                    
                    {/* Download Button */}
                    <div className="w-full">
                      <ProtectedDownloadLink 
                        href="https://r2.stellarblademod.com/UE%20Mod%20Hub%20(for%20stellar%20blade)-206-0-10-10-1749868329.exe" 
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </ProtectedDownloadLink>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="border-slate-600 text-slate-300">mod-manager</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">utility</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">nexus-mods</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">UE4SS</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Permissions */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Permissions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-xs text-slate-400">
                    <p><strong className="text-slate-300">Upload permission:</strong> Not allowed</p>
                    <p><strong className="text-slate-300">Modification permission:</strong> Permission required</p>
                    <p><strong className="text-slate-300">Conversion permission:</strong> Not allowed</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 